import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { MarketplaceCampaign } from "@redotech/redo-model/marketplace/campaigns";
import jwtDecode from "jwt-decode";
import { ReactNode, createContext, memo, useContext, useMemo } from "react";
import { RedoMarketplaceRpcClientContext } from "./rpc-client";
import { VendorAuthContext } from "./vendor-auth";

export interface CampaignContextValue {
  campaign: MarketplaceCampaign | null;
  isInCampaign: boolean;
  campaignId: number | null;
  loading: boolean;
  error: Error | null;
}

export const CampaignContext = createContext<CampaignContextValue>({
  campaign: null,
  isInCampaign: false,
  campaignId: null,
  loading: false,
  error: null,
});

export const useCampaign = () => {
  return useContext(CampaignContext);
};

export const useRequiredCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context.isInCampaign || !context.campaign) {
    throw new Error("useCampaign must be used within a campaign context");
  }
  return context;
};

/**
 * Extract campaign ID from the vendor auth JWT token
 */
function extractCampaignId(token: string): number | null {
  try {
    const decoded = jwtDecode<any>(token);
    return decoded.campaignId || null;
  } catch (error) {
    console.error("Failed to extract campaign ID from token", error);
    return null;
  }
}

export const CampaignProvider = memo(function CampaignProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const vendorAuth = useContext(VendorAuthContext);
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );

  // Extract campaign ID from the JWT token
  const campaignId = useMemo(() => {
    if (!vendorAuth?.token) return null;
    return extractCampaignId(vendorAuth.token);
  }, [vendorAuth?.token]);

  // Only fetch campaign data if we have a campaign ID
  const campaignResult = useLoad(async () => {
    if (!campaignId) return null;

    try {
      return await marketplaceRpcClient.getCampaignById({
        id: campaignId.toString(),
      });
    } catch (error) {
      console.error("Failed to fetch campaign data:", error);
      throw error;
    }
  }, [campaignId, marketplaceRpcClient]);

  const contextValue: CampaignContextValue = useMemo(
    () => ({
      campaign: campaignResult.value?.campaign || null,
      isInCampaign: campaignId !== null,
      campaignId,
      loading: campaignResult.pending,
      error: campaignResult.error,
    }),
    [
      campaignResult.value?.campaign,
      campaignId,
      campaignResult.pending,
      campaignResult.error,
    ],
  );

  return (
    <CampaignContext.Provider value={contextValue}>
      {children}
    </CampaignContext.Provider>
  );
});
